// ONE exercise-catalog loader for every surface (anti-monster S-C).
// Until 2026-06-10 the paged fetch existed twice — web-rnw's useCatalog
// and mobile's exercisesCache — and the 2026-06-09 pagination fix had
// to be applied to BOTH (the classic drift signal). This module owns
// the fetch + validation + ordering, and since #342 the cache policy too:
// platforms keep only their storage adapter (AsyncStorage on mobile,
// localStorage on web-rnw).

import { setAnimationCatalog } from "./animations.js";
import type { ApiGet, ApiGetConditional } from "./apiFetch.js";
import { paginate } from "./asyncList.js";
import { type Locale } from "./locale.js";
import { pickLocalizedText } from "./localizedText.js";
import { Exercise, type Exercise as ExerciseT } from "./schemas/exercise.js";

/** Minimal GET contract — each app injects its Cognito-token fetch. */
export type CatalogApiFetch = ApiGet;

type RawPage = { items?: unknown[]; cursor?: unknown };

// Track 0 paged contract: ?limit opts into {items, cursor}. The legacy
// bare-array read stopped at DDB's first ≤1MB page.
const pagePath = (cursor: string | null): string =>
  `/api/exercises?limit=500${
    cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""
  }`;

// Coerce a malformed cursor to null so a server-side shape bug can never
// spin the page loop to paginate()'s max-pages cap.
const pageCursor = (page: RawPage | null | undefined): string | null =>
  typeof page?.cursor === "string" ? page.cursor : null;

// Each row is zod-validated so one malformed item is dropped instead of
// poisoning consumers.
const parseRows = (raw: unknown): ExerciseT[] => {
  const out: ExerciseT[] = [];
  for (const row of Array.isArray(raw) ? raw : []) {
    const parsed = Exercise.safeParse(row);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
};

// The canonical EN-name order every surface expects. AN-11: every catalog
// that reaches a caller warms the animation table HERE, not in each caller.
const publish = (items: ExerciseT[]): ExerciseT[] => {
  items.sort((a, b) => a.name.en.localeCompare(b.name.en));
  setAnimationCatalog(items);
  return items;
};

/**
 * Fetch the COMPLETE catalog, uncached. Only for a read that must not
 * touch the athlete's own cache: the coach's `?athleteSub=` read-through.
 * Everything else goes through createExerciseCatalogSource.
 */
export async function fetchCatalogExercises(
  apiFetch: CatalogApiFetch,
): Promise<ExerciseT[]> {
  const { items } = await paginate<ExerciseT>((cursor) =>
    apiFetch<RawPage>(pagePath(cursor)).then((page) => ({
      items: parseRows(page?.items),
      cursor: pageCursor(page),
    })),
  );
  return publish(items);
}

/** One page as last received. `at` is the cursor it was requested with.
 *  `items` are the RAW rows: each build parses them itself, so a 304 never
 *  serves rows an older build's schema stripped or dropped. */
export type CatalogPage = {
  at: string | null;
  etag: string | null;
  cursor: string | null;
  items: unknown[];
};

/** The persisted blob. `fetchedAt` is the last time the server confirmed
 *  it (200 or 304); `owner` is the account it was confirmed for. */
export type CatalogSnapshot = {
  owner: string | null;
  fetchedAt: number;
  pages: CatalogPage[];
};

export type CatalogSnapshotStore = {
  read: () => Promise<unknown>;
  write: (snapshot: CatalogSnapshot) => Promise<void>;
};

/** What every catalog consumer reads through. */
export type ExerciseCatalogReader = {
  /** The signed-in account's last snapshot, from memory or disk. Never
   *  the network. */
  loadCached: () => Promise<ExerciseT[] | null>;
  /** The snapshot itself while fresh, else revalidated page by page.
   *  `force` skips the freshness window. */
  fetchAndPersist: (opts?: { force?: boolean }) => Promise<ExerciseT[]>;
};

/** The reader plus the write side's hook (the Logger's LoggerCatalogAdapter
 *  is this type). */
export type ExerciseCatalogSource = ExerciseCatalogReader & {
  /** Marks the snapshot stale: the next read revalidates. Call it after an
   *  exercise write is saved, and again when it reaches the server. */
  invalidate: () => void;
};

/** How long a confirmed catalog is served with no network at all. */
export const CATALOG_FRESH_MS = 10 * 60 * 1000;

const asSnapshot = (raw: unknown): CatalogSnapshot | null => {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<CatalogSnapshot>;
  if (typeof r.fetchedAt !== "number" || !Array.isArray(r.pages)) return null;
  if (!r.pages.every((p) => p && Array.isArray(p.items))) return null;
  return { owner: r.owner ?? null, fetchedAt: r.fetchedAt, pages: r.pages };
};

type Run = { owner: string | null; done: Promise<ExerciseT[]> };

export function createExerciseCatalogSource(deps: {
  get: ApiGetConditional;
  store: CatalogSnapshotStore;
  /** The signed-in account: a snapshot confirmed for another is never fresh. */
  owner: () => string | null;
  freshMs?: number;
  now?: () => number;
}): ExerciseCatalogSource {
  const freshMs = deps.freshMs ?? CATALOG_FRESH_MS;
  const now = deps.now ?? (() => Date.now());
  let snap: CatalogSnapshot | null = null;
  let items: ExerciseT[] | null = null;
  let diskRead: Promise<void> | null = null;
  // Concurrent callers of one account share one request.
  let inFlight: Run | null = null;
  // Bumped by invalidate(): a run that started before it lands stale.
  let epoch = 0;

  const adopt = (next: CatalogSnapshot) => {
    const parsed = publish(next.pages.flatMap((p) => parseRows(p.items)));
    snap = next;
    items = parsed;
  };

  const hydrate = (): Promise<void> => {
    if (!diskRead) {
      diskRead = deps.store.read().then(
        (raw) => {
          const s = asSnapshot(raw);
          if (!s || snap) return;
          try {
            adopt(s);
          } catch {
            // a malformed blob is no cache: the network fills it
          }
        },
        () => undefined,
      );
    }
    return diskRead;
  };

  const ownSnap = (): boolean =>
    snap !== null && snap.owner !== null && snap.owner === deps.owner();

  const isFresh = (): boolean => {
    if (!snap || !ownSnap()) return false;
    const age = now() - snap.fetchedAt;
    return age >= 0 && age < freshMs;
  };

  const revalidate = async (owner: string | null): Promise<ExerciseT[]> => {
    await hydrate();
    const startEpoch = epoch;
    const prior = snap;
    const byAt = new Map<string | null, CatalogPage>();
    for (const p of prior?.pages ?? []) byAt.set(p.at, p);
    const { items: pages } = await paginate<CatalogPage>(async (at) => {
      const known = byAt.get(at);
      const res = await deps.get<RawPage>(pagePath(at), {
        ifNoneMatch: known?.etag ?? null,
      });
      if (res.status === 304) {
        if (!known) throw new Error("catalog_unexpected_304");
        return { items: [known], cursor: known.cursor };
      }
      const page: CatalogPage = {
        at,
        etag: res.etag,
        cursor: pageCursor(res.data),
        items: Array.isArray(res.data?.items) ? res.data.items : [],
      };
      return { items: [page], cursor: page.cursor };
    });
    // Signed out or switched while the request ran: this answer is not
    // the current account's, so it is neither kept nor returned.
    if (deps.owner() !== owner) throw new Error("catalog_owner_changed");
    const next: CatalogSnapshot = {
      owner,
      fetchedAt: epoch === startEpoch ? now() : 0,
      pages,
    };
    const unchanged =
      prior !== null &&
      pages.length === prior.pages.length &&
      pages.every((p, i) => p === prior.pages[i]);
    if (unchanged) snap = next;
    else adopt(next);
    await deps.store.write(next).catch(() => undefined);
    return items ?? [];
  };

  const loadCached = async (): Promise<ExerciseT[] | null> => {
    await hydrate();
    // Another account's catalogue is never shown: the network fills it.
    return items && ownSnap() ? [...items] : null;
  };

  const fetchAndPersist = async ({
    force = false,
  }: { force?: boolean } = {}): Promise<ExerciseT[]> => {
    const owner = deps.owner();
    if (!force) {
      if (inFlight?.owner === owner) return [...(await inFlight.done)];
      await hydrate();
      if (isFresh() && items) return [...items];
      if (inFlight?.owner === owner) return [...(await inFlight.done)];
    }
    // A forced read waits out the one in flight: that one may predate the
    // write the caller just made.
    const before = inFlight?.done ?? Promise.resolve([]);
    const run: Run = {
      owner,
      done: before.then(
        () => revalidate(owner),
        () => revalidate(owner),
      ),
    };
    inFlight = run;
    const clear = () => {
      if (inFlight === run) inFlight = null;
    };
    run.done.then(clear, clear);
    return [...(await run.done)];
  };

  const invalidate = () => {
    epoch++;
    void hydrate()
      .then(() => {
        if (!snap) return;
        snap = { ...snap, fetchedAt: 0 };
        return deps.store.write(snap);
      })
      .catch(() => undefined);
  };

  return { loadCached, fetchAndPersist, invalidate };
}

/** Resolve catalog rows to the {slug, name} pairs pickers want
 *  (@heraion/ui CatalogItem is this exact structural shape). Input
 *  order is preserved: the loader already sorted by the stable EN
 *  canonical, so locale switches don't reshuffle the list. */
export const toCatalogItems = (
  items: ReadonlyArray<ExerciseT>,
  locale: Locale,
): Array<{ slug: string; name: string }> =>
  items.map((ex) => ({
    slug: ex.slug,
    name: pickLocalizedText(ex.name, locale),
  }));
